import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Collapse,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { LiaFileDownloadSolid } from 'react-icons/lia';
import { useSelector } from 'react-redux';
import { getLabelsByInstituteType } from '../../../utils/constants';

const ScheduleTable = ({
  schedules,
  onEditSchedule,
  onDeleteSchedule,
  formatDate,
  formatTime,
  examTypes = [],
  subjects = [],
  onScheduleDownload,
}) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  let { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });
  let { Department, Class, Section } = getLabelsByInstituteType(instituteType);

  // Helper function to get exam type name by ID
  const getExamTypeName = (examTypeId) => {
    const examType = examTypes.find((type) => type.id === examTypeId);
    return examType ? examType.examType : examTypeId || '-';
  };

  // Helper function to get subject name by ID
  const getSubjectName = (subjectId) => {
    const subject = subjects.find((subj) => subj.id === subjectId);
    return subject ? subject.subjectName : subjectId || '-';
  };

  // Group schedules by exam type and department/class
  const groupSchedules = () => {
    const groups = {};

    schedules.forEach((schedule, scheduleIndex) => {
      const groupKey = `${schedule.examType}-${schedule.departmentName}-${schedule.classesName}-${schedule.sectionName || 'all'}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          examType: schedule.examType,
          departmentName: schedule.departmentName,
          classesName: schedule.classesName,
          sectionName: schedule.sectionName,
          subjects: [],
          totalMarks: 0,
          groupKey: groupKey, // Store the key within the group
          firstScheduleIndex: scheduleIndex, // For additional uniqueness
        };
      }

      groups[groupKey].subjects.push(schedule);
      groups[groupKey].totalMarks += schedule.marks || 0;
    });

    return Object.keys(groups)
      .sort() // Sort for consistent ordering
      .map((key, index) => ({
        ...groups[key],
        serialNumber: index + 1,
        uniqueKey: `group-${index}-${key}`, // Create a truly unique key
      }));
  };

  const toggleExpanded = (groupKey) => {
    console.log('Toggle expanded called with groupKey:', groupKey);
    console.log('Current expanded groups:', Array.from(expandedGroups));

    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
      console.log('Removing from expanded:', groupKey);
    } else {
      newExpanded.add(groupKey);
      console.log('Adding to expanded:', groupKey);
    }

    console.log('New expanded groups:', Array.from(newExpanded));
    setExpandedGroups(newExpanded);
  };

  const groupedSchedules = groupSchedules();
  if (schedules.length === 0) {
    return (
      <Box className='text-center py-8'>
        <ScheduleIcon style={{ fontSize: 48, color: '#e5e7eb' }} />
        <Typography variant='h6' className='text-gray-400'>
          No Examination Schedules Found
        </Typography>
      </Box>
    );
  }

  const handleDownloadSchedule = (group) => {
    if (onScheduleDownload) {
      onScheduleDownload(group);
    } else {
      console.log('Download schedule clicked');
    }
  };

  return (
    <>
      <Typography variant='h6' className='mb-4 text-blue-700 font-semibold'>
        Examination Schedule
      </Typography>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: '70vh', overflow: 'auto' }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <TableCell width='60'>
                <strong>S.No</strong>
              </TableCell>
              <TableCell>
                <strong>Exam</strong>
              </TableCell>
              <TableCell align='center'>
                <strong>
                  {Department}/{Class}
                </strong>
              </TableCell>
              <TableCell align='center'>
                <strong>Total Marks</strong>
              </TableCell>
              <TableCell align='center'>
                <strong>Subjects</strong>
              </TableCell>
              <TableCell align='center'>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedSchedules.map((group) => (
              <React.Fragment key={group.uniqueKey}>
                {/* Main Group Row */}
                <TableRow
                  hover
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: expandedGroups.has(group.groupKey)
                      ? '#f5f5f5'
                      : 'inherit',
                    '&:hover': {
                      backgroundColor: '#e8f5e8',
                    },
                  }}
                  onClick={() => {
                    console.log('Clicked row with groupKey:', group.groupKey);
                    toggleExpanded(group.groupKey);
                  }}
                >
                  <TableCell>
                    <Box display='flex' alignItems='center'>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log(
                            'Icon clicked with groupKey:',
                            group.groupKey,
                          );
                          toggleExpanded(group.groupKey);
                        }}
                      >
                        {expandedGroups.has(group.groupKey) ? (
                          <KeyboardArrowDown />
                        ) : (
                          <KeyboardArrowRight />
                        )}
                      </IconButton>
                      {group.serialNumber}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant='body2' className='font-medium'>
                        {getExamTypeName(group.examType)}
                      </Typography>
                      <Typography variant='caption' className='text-gray-600'>
                        {group.subjects.length} subjects
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align='center'>
                    <Box>
                      <Typography variant='body2' className='font-medium'>
                        {group.departmentName}
                      </Typography>
                      <Typography variant='caption' className='text-gray-600'>
                        {group.classesName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={`${group.totalMarks} Marks`}
                      color='primary'
                      variant='outlined'
                      size='small'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={`${group.subjects.length} Subjects`}
                      color='secondary'
                      variant='outlined'
                      size='small'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={() => handleDownloadSchedule(group)}
                    >
                      <LiaFileDownloadSolid size={25} />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* Expandable Subject Details */}
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={6}
                  >
                    <Collapse
                      in={expandedGroups.has(group.groupKey)}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Box sx={{ margin: 1 }}>
                        <Typography
                          variant='h6'
                          gutterBottom
                          component='div'
                          color='primary'
                        >
                          Subject Details
                        </Typography>
                        <Table size='small'>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                              <TableCell>
                                <strong>Subject</strong>
                              </TableCell>
                              <TableCell align='center'>
                                <strong>Date</strong>
                              </TableCell>
                              <TableCell align='center'>
                                <strong>Time</strong>
                              </TableCell>
                              <TableCell align='center'>
                                <strong>Marks</strong>
                              </TableCell>
                              <TableCell align='center'>
                                <strong>Pass Marks</strong>
                              </TableCell>
                              {instituteType?.toLowerCase() === 'college' && (
                                <TableCell align='center'>
                                  <strong>{Section}</strong>
                                </TableCell>
                              )}
                              <TableCell align='center'>
                                <strong>Actions</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.subjects.map((subject) => (
                              <TableRow key={subject.id} hover>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    className='font-medium'
                                  >
                                    {getSubjectName(subject.subjectName)}
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <Typography variant='body2'>
                                    {formatDate(subject.date)}
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <Typography variant='body2'>
                                    {subject.startTime && subject.endTime
                                      ? `${formatTime(subject.startTime)} - ${formatTime(subject.endTime)}`
                                      : subject.startTime
                                        ? `${formatTime(subject.startTime)} onwards`
                                        : 'Not scheduled'}
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <Chip
                                    label={subject.marks}
                                    color='primary'
                                    size='small'
                                  />
                                </TableCell>
                                <TableCell align='center'>
                                  <Chip
                                    label={subject.passMarks}
                                    color='success'
                                    variant='outlined'
                                    size='small'
                                  />
                                </TableCell>
                                {instituteType?.toLowerCase() === 'college' && (
                                  <TableCell align='center'>
                                    <Typography variant='body2'>
                                      {subject.sectionName || 'All Sections'}
                                    </Typography>
                                  </TableCell>
                                )}
                                <TableCell align='center'>
                                  <Box className='flex gap-1 justify-center'>
                                    <IconButton
                                      size='small'
                                      color='primary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onEditSchedule(subject);
                                      }}
                                    >
                                      <EditIcon fontSize='small' />
                                    </IconButton>
                                    <IconButton
                                      size='small'
                                      color='error'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteSchedule(subject.id);
                                      }}
                                    >
                                      <DeleteIcon fontSize='small' />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ScheduleTable;
